import React from 'react';
import { Box, Divider, Flex } from '@withjoy/joykit';
import { ListingPhotos } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/ListingPhotos';
import { HeaderBlock } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/HeaderBlock';
import { TravelLogistics } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/TravelLogistics';
import { CostBreakdown } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/CostBreakdown';
import { HotelCTA } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/HotelCTA';
import { CouplesPick } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/CouplesPick';
import { SponsoredTag } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/SponsoredTag';
import { UserNote } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/UserNote';
import { OtaProviderName } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/OtaProviderName';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { ListingContainer, ContentPanel, LogisticsAndCostBlock, CutoffDate } from './ListingEntryFrame';
import { useListingEntryBase } from './useListingEntryBase';
import type { ScenarioListingEntryProps } from './ListingEntry.types';
import { ListingEntryMobileV2 } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/components/ListingEntryMobileV2';
import { DynamicMargin } from '@apps/stays/EventAccommodations/routes/shared/hotelOfferDiagnostics/DynamicMargin';
import { UserRating } from '@apps/stays/components/Ratings';

// Guest scenario: shows Couple's Pick tags, room block notes + cutoff dates,
// "See Availability" CTA, no "Added" tag, no sold-out CTA.
const GuestListingEntryInner = (props: ScenarioListingEntryProps) => {
  const base = useListingEntryBase(props);
  const {
    listing,
    state,
    noOfNights,
    sourceName,
    redirectUrl,
    isSoldOut,
    isLinkEnabled,
    isDesktopLayout,
    shouldUseNewMobileLayout,
    variant,
    pointOfInterest,
    handleSeeAvailability,
    handlePhotoClick,
    containerRef,
  } = base;

  const shouldShowHotelCta = redirectUrl && ((listing.type === 'standardHotel' && !listing.isSoldOutForSelectedDates) || listing.type === 'roomBlock');

  if (shouldUseNewMobileLayout) {
    return (
      <div ref={containerRef}>
        <ListingEntryMobileV2
          state={state}
          listing={listing}
          variant={variant}
          noOfNights={noOfNights}
          sourceName={sourceName}
          showRoomBlockNoteAndDate={true}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <ListingContainer
        id={`listing-${listing.joyPlaceId}`}
        joyPlaceId={listing.joyPlaceId}
      >
        <Flex
          position="relative"
          onTouchEnd={e => e.stopPropagation()}
          onTouchMove={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onClick={handlePhotoClick}
          cursor={isLinkEnabled && redirectUrl ? 'pointer' : 'default'}
        >
          <ListingPhotos
            listing={listing}
            isMobileOrTablet={!isDesktopLayout}
            showAddedTag={false}
          />
          {listing.isSponsored && <SponsoredTag />}
        </Flex>
        <Box
          width={'100%'}
          {...(isLinkEnabled && redirectUrl ? { as: 'a', href: redirectUrl, target: '_blank', onClick: handleSeeAvailability } : {})}
        >
          <Flex
            flexDirection="column"
            width={'100%'}
            height={'100%'}
            justifyContent={'space-between'}
          >
            <ContentPanel>
              {listing.isPickedByCouple && (
                <CouplesPick
                  listing={listing}
                  isAdmin={false}
                  showCouplesPicksTag={true}
                />
              )}
              <HeaderBlock
                listing={listing}
                pointOfInterest={pointOfInterest}
                isMobileOrTablet={!isDesktopLayout}
                redirectUrl={redirectUrl}
                isLinkEnabled={isLinkEnabled}
              />
              <OtaProviderName otaProvider={listing.otaProvider} />
              {listing.type === 'roomBlock' && listing.userNote && <UserNote note={listing.userNote} />}
              {!isDesktopLayout && <Divider marginY={3} />}
              {isDesktopLayout ? (
                <Flex
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  marginTop={4}
                >
                  <Flex
                    flexDirection="column"
                    rowGap={3}
                  >
                    {pointOfInterest && (
                      <TravelLogistics
                        pointOfInterest={pointOfInterest}
                        listing={listing}
                      />
                    )}
                    {listing.userRating && (
                      <UserRating
                        rating={listing.userRating.ratingValue}
                        reviewCount={listing.userRating.ratingCount}
                      />
                    )}
                  </Flex>
                  <Flex
                    flexDirection="column"
                    gap={5}
                  >
                    {listing.type === 'roomBlock' && <CutoffDate cutoffDate={listing.cutoffDate} />}
                    {listing.pricingInfo.isLoading ? (
                      <CostBreakdown.Skeleton />
                    ) : listing.pricingInfo.values ? (
                      <CostBreakdown
                        occupancies={state.occupancies}
                        pricingInfo={listing.pricingInfo.values}
                        noOfNights={noOfNights}
                      />
                    ) : null}
                  </Flex>
                </Flex>
              ) : (
                <LogisticsAndCostBlock>
                  {pointOfInterest && (
                    <TravelLogistics
                      pointOfInterest={pointOfInterest}
                      listing={listing}
                    />
                  )}
                  {listing.userRating && (
                    <UserRating
                      rating={listing.userRating.ratingValue}
                      reviewCount={listing.userRating.ratingCount}
                    />
                  )}
                  <Flex
                    flexDirection={'column'}
                    gap={5}
                  >
                    {listing.type === 'roomBlock' && <CutoffDate cutoffDate={listing.cutoffDate} />}
                    {listing.pricingInfo.isLoading ? (
                      <CostBreakdown.Skeleton />
                    ) : listing.pricingInfo.values ? (
                      <CostBreakdown
                        occupancies={state.occupancies}
                        pricingInfo={listing.pricingInfo.values}
                        noOfNights={noOfNights}
                      />
                    ) : null}
                  </Flex>
                </LogisticsAndCostBlock>
              )}
              {isSoldOut && (
                <Box marginTop={{ _: 0, sm2: 5 }}>
                  <SoldOutTag eventDate={createDateFromUnformattedString(state.searchDates.checkin)} />
                </Box>
              )}
              <DynamicMargin
                diag={listing.diag}
                otaLinkMode="fallback"
              />
            </ContentPanel>
            {!isDesktopLayout && shouldShowHotelCta ? (
              <Flex
                justifyContent={'flex-end'}
                width={'100%'}
                marginTop={5}
              >
                <HotelCTA
                  listing={listing}
                  redirectUrl={redirectUrl}
                  sourceName={sourceName}
                />
              </Flex>
            ) : null}
          </Flex>
        </Box>
      </ListingContainer>
    </div>
  );
};

export const GuestListingEntry = React.memo(GuestListingEntryInner);
